import { useEffect, useState } from 'react';

export const ROWS_MIN = 1;
export const ROWS_MAX = 6;
export const TEXTAREA_LINE_HEIGHT = 24;

export interface UseRowsProps {
  value: string | number | readonly string[] | undefined;
  textAreaLineHeight?: number;
  defaultRows?: number;
  maxRows?: number;
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>;
  compactMode?: boolean;
}

export const useRows = ({
  value,
  textAreaLineHeight = TEXTAREA_LINE_HEIGHT,
  defaultRows,
  maxRows = ROWS_MAX,
  textAreaRef,
  compactMode = false,
}: UseRowsProps) => {
  const defaultRowsValue = defaultRows ?? ROWS_MIN;

  const [rows, setRows] = useState(defaultRowsValue);
  useEffect(() => {
    if (!value) {
      setRows(defaultRowsValue);
    }
  }, [value, defaultRowsValue]);

  const updateRows = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const previousRows = event.target.rows;
    event.target.rows = defaultRowsValue;

    const rawRows = Math.ceil(event.target.scrollHeight / textAreaLineHeight);

    const currentRows = Math.max(
      defaultRowsValue,
      compactMode ? rawRows - 1 : rawRows
    );

    if (currentRows === previousRows) {
      event.target.rows = currentRows;
    }

    if (currentRows >= maxRows) {
      event.target.rows = maxRows;
      event.target.scrollTop = event.target.scrollHeight;
    }

    setRows(currentRows < maxRows ? currentRows : maxRows);
  };

  useEffect(() => {
    const el = textAreaRef?.current;

    if (!el || !value) {
      return;
    }

    const previousRows = el.rows;
    el.rows = defaultRowsValue;

    const rawRows = Math.ceil(el.scrollHeight / textAreaLineHeight);
    const currentRows = Math.max(
      defaultRowsValue,
      compactMode ? rawRows - 1 : rawRows
    );

    if (currentRows === previousRows) {
      el.rows = currentRows;
    }

    if (currentRows >= maxRows) {
      el.rows = maxRows;
      el.scrollTop = el.scrollHeight;
      setRows(maxRows);
    } else {
      el.rows = currentRows;
      setRows(currentRows);
    }
  }, [value, textAreaRef, textAreaLineHeight, maxRows]);

  return { rows, updateRows };
};
